/*
 * assets/header.js — Boostly header behaviour.
 * Replaces theme.Header / theme.MobileNav / theme.Search / theme.Drawers of the original theme.js
 * and the hover tooltip JS of header_strip.liquid.
 *   <boostly-header>        sticky header (adds .is-sticky / .is-hidden on scroll), dropdowns (hover + click + keyboard)
 *   <boostly-mobile-menu>   drawer menu (Theme.drawer); closes when viewport ≥ 990px (desktop header)
 *   <boostly-search-drawer> predictive search in the top drawer
 */
(function () {
  'use strict';
  const define = (n, c) => { if (!customElements.get(n)) customElements.define(n, c); };

  class BoostlyHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this;
      this.items = Array.from(this.querySelectorAll('[data-dropdown]'));
      this.items.forEach((item) => this.bindDropdown(item));
      document.addEventListener('click', this.onDocClick = (e) => { if (!this.contains(e.target)) this.closeAll(); });
      document.addEventListener('keydown', this.onKey = (e) => { if (e.key === 'Escape') this.closeAll(true); });
      if (this.dataset.sticky === 'true') this.initSticky();
      this.setHeight();
      window.addEventListener('resize', this.onResize = Theme.debounce(() => this.setHeight(), 150));
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKey);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
    }
    setHeight() { document.documentElement.style.setProperty('--header-height', this.offsetHeight + 'px'); }
    bindDropdown(item) {
      const link = item.querySelector(':scope > .site-nav__link');
      let timer;
      const open = () => { clearTimeout(timer); this.closeAll(false, item); item.classList.add('is-open'); link.setAttribute('aria-expanded', 'true'); };
      const close = () => { item.classList.remove('is-open'); link.setAttribute('aria-expanded', 'false'); };
      item.addEventListener('mouseenter', () => { if (window.matchMedia('(hover: hover)').matches) open(); });
      item.addEventListener('mouseleave', () => { timer = setTimeout(close, 150); });
      // First tap/click opens, second follows the link (touch devices; original behaviour).
      link.addEventListener('click', (e) => {
        if (!item.classList.contains('is-open')) { e.preventDefault(); open(); }
      });
      link.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || (e.key === ' ' )) { e.preventDefault(); open(); const f = item.querySelector('.site-nav__dropdown a'); if (f) f.focus(); }
      });
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
    }
    closeAll(restoreFocus, except) {
      this.items.forEach((i) => {
        if (i === except) return;
        if (restoreFocus && i.classList.contains('is-open')) i.querySelector('.site-nav__link').focus();
        i.classList.remove('is-open');
        const l = i.querySelector(':scope > .site-nav__link'); if (l) l.setAttribute('aria-expanded', 'false');
      });
    }
    initSticky() {
      this.section.classList.add('section-header--sticky');
      let lastY = window.scrollY;
      this.onScroll = Theme.throttle(() => {
        const y = window.scrollY;
        const past = y > this.offsetHeight + 40;
        this.section.classList.toggle('is-sticky', past);
        // hide on scroll down, reveal on scroll up (only once past the header)
        this.section.classList.toggle('is-hidden', past && y > lastY && !document.body.classList.contains('drawer-open'));
        lastY = y;
      }, 80);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }
  define('boostly-header', BoostlyHeader);

  class BoostlyMobileMenu extends HTMLElement {
    connectedCallback() {
      this.mq = window.matchMedia('(min-width: 990px)');
      this.onMq = (e) => { if (e.matches && this.classList.contains('is-open')) Theme.drawer.close('mobile-menu'); };
      this.mq.addEventListener('change', this.onMq);
    }
    disconnectedCallback() { this.mq.removeEventListener('change', this.onMq); }
  }
  define('boostly-mobile-menu', BoostlyMobileMenu);

  class BoostlySearchDrawer extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', Theme.debounce(() => this.search(), 250));
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; return; }
      const route = (Theme.routes.predictiveSearch || '/search/suggest');
      try {
        const res = await fetch(`${route}.json?q=${encodeURIComponent(q)}&resources[type]=product,collection,article,page&resources[limit]=6`);
        const data = await res.json();
        this.render(data.resources && data.resources.results || {}, q);
      } catch (e) { this.results.innerHTML = ''; }
    }
    render(r, q) {
      const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const products = r.products || [];
      const others = [].concat(r.collections || [], r.pages || [], r.articles || []);
      if (!products.length && !others.length) {
        this.results.innerHTML = `<p class="search-drawer__empty">${esc(Theme.strings.noResults || 'No results')}</p>`; return;
      }
      let html = '<div class="predictive">';
      if (products.length) {
        html += '<ul class="predictive__products list-unstyled">' + products.map((p) => `
          <li><a href="${p.url}" class="predictive__product">
            ${p.image ? `<img src="${p.image}&width=120" alt="" width="60" height="60" loading="lazy">` : ''}
            <span><span class="predictive__title">${esc(p.title)}</span><span class="predictive__price">${Theme.money.format(Math.round(parseFloat(p.price) * 100))}</span></span>
          </a></li>`).join('') + '</ul>';
      }
      if (others.length) {
        html += '<ul class="predictive__links list-unstyled">' + others.map((o) => `<li><a href="${o.url}">${esc(o.title)}</a></li>`).join('') + '</ul>';
      }
      html += `<a class="btn btn--secondary btn--full predictive__all" href="${Theme.routes.search || '/search'}?q=${encodeURIComponent(q)}">${esc(Theme.strings.searchAll || 'View all results')}</a></div>`;
      this.results.innerHTML = html;
    }
  }
  define('boostly-search-drawer', BoostlySearchDrawer);
})();
